import React, { useState, useRef, useEffect } from "react";

const CalendarPopup = ({ currentDate, onDateSelect, onClose, isOpen }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const calendarRef = useRef(null);

  // Sample attendance data - replace with actual data
  const attendanceData = {
    present: [],
    late: [],
    absent: [],
    leave: [],
    overtime: [],
    fine: [],
  };

  // Summary counts
  const summaryData = {
    present: attendanceData.present.length,
    late: attendanceData.late.length,
    absent: attendanceData.absent.length,
    leave: attendanceData.leave.length,
    overtime: attendanceData.overtime.length,
    fine: attendanceData.fine.length,
  };

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const getAttendanceStatus = (day) => {
    if (attendanceData.present.includes(day)) return "present";
    if (attendanceData.late.includes(day)) return "late";
    if (attendanceData.absent.includes(day)) return "absent";
    if (attendanceData.leave.includes(day)) return "leave";
    if (attendanceData.overtime.includes(day)) return "overtime";
    if (attendanceData.fine.includes(day)) return "fine";
    return null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "present":
        return "bg-green-500 text-white";
      case "late":
        return "bg-yellow-500 text-white";
      case "absent":
        return "bg-red-500 text-white";
      case "leave":
        return "bg-orange-500 text-white";
      case "overtime":
        return "bg-purple-500 text-white";
      case "fine":
        return "bg-blue-500 text-white";
      default:
        return "hover:bg-gray-100";
    }
  };

  // Navigate to previous month
  const handlePrevMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  // Navigate to next month (disable future months beyond today)
  const handleNextMonth = () => {
    const today = new Date();
    if (selectedYear >= today.getFullYear() && selectedMonth >= today.getMonth()) {
      return; // Disable navigation to future months
    }
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const handleDateClick = (day) => {
    if (day == null || selectedMonth == null || selectedYear == null) return;

    const selectedDate = new Date(selectedYear, selectedMonth, day);
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Allow selection of today

    if (selectedDate > today) return;

    // Format date as DD/MM/YYYY to match UserInfoPage
    const formattedDate = `${day.toString().padStart(2, "0")}/${(selectedMonth + 1)
      .toString()
      .padStart(2, "0")}/${selectedYear}`;
    onDateSelect(formattedDate);
    onClose();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
  const firstDay = getFirstDayOfMonth(selectedMonth, selectedYear);
  const days = [];

  // Add empty slots for days before the first day of the month
  for (let i = 0; i < firstDay; i++) {
    days.push(
      <div key={`empty-${i}`} className="w-10 h-10"></div>
    );
  }

  // Generate calendar days
  for (let day = 1; day <= daysInMonth; day++) {
    const status = getAttendanceStatus(day);
    const colorClass = getStatusColor(status);
    const isFuture = new Date(selectedYear, selectedMonth, day) > new Date();

    days.push(
      <button
        key={day}
        onClick={() => !isFuture && handleDateClick(day)}
        disabled={isFuture}
        className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${colorClass} ${
          isFuture ? "opacity-40 cursor-not-allowed" : ""
        }`}
      >
        {day}
      </button>
    );
  }

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-30 flex items-start justify-center pt-20 z-50 ${isOpen ? "" : "hidden"}`}>
      <div ref={calendarRef} className="bg-white rounded-2xl shadow-2xl p-6 w-96 animate-in fade-in-0 zoom-in-95 duration-200">
        {/* Header with summary stats */}
        {/* <div className="flex items-center justify-between mb-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{summaryData.present.toString().padStart(2, "0")}</div>
            <div className="text-xs text-green-600">Present</div>
          </div>
          <div className="w-px h-12 bg-gray-300"></div>
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">{summaryData.late.toString().padStart(2, "0")}</div>
            <div className="text-xs text-yellow-600">Late</div>
          </div>
          <div className="w-px h-12 bg-gray-300"></div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{summaryData.absent.toString().padStart(2, "0")}</div>
            <div className="text-xs text-red-600">Absent</div>
          </div>
          <div className="w-px h-12 bg-gray-300"></div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{summaryData.leave.toString().padStart(2, "0")}</div>
            <div className="text-xs text-orange-600">Leave</div>
          </div>
        </div> */}

        {/* Bottom row - 2 items */}
        {/* <div className="flex items-center justify-center gap-16 mb-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{summaryData.overtime.toString().padStart(2, "0")}</div>
            <div className="text-xs text-purple-600">Overtime</div>
          </div>
          <div className="w-px h-12 bg-gray-300"></div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{summaryData.fine.toString().padStart(2, "0")}</div>
            <div className="text-xs text-blue-600">Fine</div>
          </div>
        </div> */}

        {/* Month/Year navigation */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={handlePrevMonth}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="text-lg font-semibold text-blue-600">
            {months[selectedMonth]} {selectedYear}
          </h3>
          <button
            onClick={handleNextMonth}
            disabled={selectedYear >= new Date().getFullYear() && selectedMonth >= new Date().getMonth()}
            className={`p-2 rounded-full transition-colors ${
              selectedYear >= new Date().getFullYear() && selectedMonth >= new Date().getMonth()
                ? "opacity-40 cursor-not-allowed"
                : "hover:bg-gray-100"
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Days of week header */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((day) => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {days}
        </div>
      </div>
    </div>
  );
};

export default CalendarPopup;